import { Link } from "react-router-dom";
import { isPlayStoreApp } from "@/lib/apkVersion";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { BookOpen, HandHeart, ArrowRight, Repeat, Clapperboard, Star, Landmark, MapPin, Sparkles, Package, HardDriveDownload, SunMoon, Compass, GraduationCap, CalendarDays, Library, Smartphone, HeartHandshake, LayoutGrid } from "lucide-react";
import { ACCOUNT_UI } from "@/data/accountI18n";
import { TR } from "@/data/tr";
import { INSTALL_UI } from "@/data/installI18n";
import { UI_EXTRA } from "@/data/uiExtra";
import { ZIKIR_UI } from "@/data/ui/zikirUi";
import { ADHKAR_UI } from "@/data/ui/adhkarUi";
import { UM_UI } from "@/data/ui/umrahMandiriUi";
import { APPS_UI } from "@/data/ui/appsUi";
import { BELAJAR_UI } from "@/data/belajarI18n";
import { HAJJ_UI } from "@/data/ui/hajjTimelineUi";
import { REF_UI } from "@/data/ui/manasikRefUi";

const GOLD = "border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5";
const GOLD_ICON = "bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]";

// Wide row card (icon · label/hint · arrow). `tone` = gold for the highlighted worship features.
const RowCard = ({ to, testid, icon: Icon, label, hint, gold }) => (
  <Link to={to} className="col-span-2 md:col-span-3">
    <motion.div whileHover={{ y: -3 }}>
      <Card className={`rounded-3xl shadow-sm p-4 sm:p-5 h-full flex items-center gap-3 sm:gap-4 ${gold ? GOLD : "border-border/50"}`} data-testid={testid}>
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${gold ? GOLD_ICON : "bg-primary/10 text-primary"}`}><Icon className="h-6 w-6" /></span>
        <div className="min-w-0">
          <p className="font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground ms-auto shrink-0 rtl:-scale-x-100" />
      </Card>
    </motion.div>
  </Link>
);

const Tile = ({ to, testid, icon: Icon, label, hint, className = "" }) => (
  <Link to={to} className={className}>
    <motion.div whileHover={{ y: -3 }} className="h-full">
      <Card className="rounded-3xl border-border/50 shadow-sm p-4 sm:p-5 h-full" data-testid={testid}>
        <Icon className="h-6 w-6 text-primary mb-4 sm:mb-8" />
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </Card>
    </motion.div>
  </Link>
);

// Feature shortcuts of the home page (order matters — most used first). Also feeds the quick-search index.
export function buildQuickLinks(t, tc, totalStages) {
  const dash = (key) => ({ label: tc(TR[`dash.${key}.label`]), hint: tc(TR[`dash.${key}.hint`]) });
  const tiles = [
    { to: "/guide", testid: "quick-guide", icon: BookOpen, label: t("guide"), hint: `${totalStages} ${t("steps")}` },
    { to: "/prayers", testid: "quick-prayers", icon: HandHeart, label: t("prayers"), hint: t("viewPrayers") },
    { to: "/tasbih", testid: "quick-tasbih", icon: Repeat, label: t("tasbih"), hint: t("rounds"), className: "col-span-2 md:col-span-1" },
  ];
  const rows = [
    { to: "/videos", testid: "quick-videos", icon: Clapperboard, label: t("videoGuides"), hint: t("videoGuidesHint") },
    { to: "/zikir", testid: "quick-zikir", icon: Sparkles, label: tc(ZIKIR_UI.dashLabel), hint: tc(ZIKIR_UI.dashHint), gold: true },
    { to: "/pagi-petang", testid: "quick-adhkar", icon: SunMoon, label: tc(ADHKAR_UI.dashLabel), hint: tc(ADHKAR_UI.dashHint), gold: true },
    { to: "/umrah-mandiri", testid: "quick-umrah-mandiri", icon: Compass, label: tc(UM_UI.dashLabel), hint: tc(UM_UI.dashHint), gold: true },
    { to: "/apps", testid: "quick-apps", icon: LayoutGrid, label: tc(APPS_UI.dashLabel), hint: tc(APPS_UI.dashHint), gold: true },
    { to: "/belajar", testid: "quick-belajar", icon: GraduationCap, label: tc(BELAJAR_UI.dashLabel), hint: tc(BELAJAR_UI.dashHint) },
    { to: "/haji-timeline", testid: "quick-hajj-timeline", icon: CalendarDays, label: tc(HAJJ_UI.dashLabel), hint: tc(HAJJ_UI.dashHint) },
    { to: "/manasik-referensi", testid: "quick-manasik-ref", icon: Library, label: tc(REF_UI.dashLabel), hint: tc(REF_UI.dashHint) },
    { to: "/packages", testid: "quick-packages", icon: Package, label: tc(UI_EXTRA["pkg.title"]), hint: tc(UI_EXTRA["pkg.dashHint"]) },
    { to: "/keutamaan", testid: "quick-keutamaan", icon: Star, ...dash("keutamaan") },
    { to: "/quran", testid: "quick-quran", icon: BookOpen, ...dash("quran") },
    { to: "/ziyarah", testid: "quick-ziyarah", icon: Landmark, ...dash("ziyarah") },
    { to: "/haram", testid: "quick-haram", icon: MapPin, ...dash("haram") },
    { to: "/nabawi", testid: "quick-nabawi", icon: Landmark, ...dash("nabawi") },
    { to: "/baqi", testid: "quick-baqi", icon: MapPin, ...dash("baqi") },
    { to: "/jenazah", testid: "quick-jenazah", icon: HandHeart, ...dash("jenazah") },
    { to: "/sunnah", testid: "quick-sunnah", icon: Sparkles, ...dash("sunnah") },
    { to: "/downloads", testid: "quick-downloads", icon: HardDriveDownload, label: tc({ id: "Unduhan Offline", en: "Offline Downloads", ar: "التنزيلات دون اتصال" }), hint: tc({ id: "Kelola audio kisah & doa tersimpan", en: "Manage saved story & dua audio", ar: "إدارة الصوتيات المحفوظة" }) },
    { to: "/install", testid: "quick-install", icon: Smartphone, label: tc(INSTALL_UI.homeTitle), hint: tc(INSTALL_UI.homeHint), gold: true },
    { to: "/support", testid: "quick-support", icon: HeartHandshake, label: tc(ACCOUNT_UI.supportTitle), hint: tc(ACCOUNT_UI.homeHint), gold: true },
  ].filter((r) => !(isPlayStoreApp() && (r.to === "/support" || r.to === "/install")));
  return { tiles, rows };
}

export function QuickLinks({ t, tc, totalStages }) {
  const { tiles, rows } = buildQuickLinks(t, tc, totalStages);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {tiles.map((tile) => <Tile key={tile.to} {...tile} />)}
      {rows.map((r) => <RowCard key={r.to} {...r} />)}
    </div>
  );
}
